import { memo, useCallback, useId, useState, type FC } from "react";
import { ChevronDown, ChevronUp, ExternalLink, ImageIcon, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import MiniSpinner from "src/components/reusable/Spinner/MiniSpinner";
import type { RecommendedProduct } from "src/services/CampaignAPI";
import { formatNumber } from "src/utils/numberFormat";
import type { RecommendedProductsState } from "../hooks/UseRecommendedProducts";


const PRODUCTS_KEY = "campaignFlow.aiWizard.recommendedProducts";
const PERCENT_FORMAT: Intl.NumberFormatOptions = { style: "percent", maximumFractionDigits: 0 };

interface ProductCardProps {
  product: RecommendedProduct;
  onInsert?: (_product: RecommendedProduct) => void;
  showMatch: boolean;
}

const ProductCard: FC<ProductCardProps> = ({ product, onInsert, showMatch }) => {
  const { t, i18n } = useTranslation();
  const { name, product_type: productType, image_url: imageUrl, product_url: productUrl } = product;
  const share = formatNumber(product.share, PERCENT_FORMAT, i18n.language);

  const handleInsert = useCallback(() => onInsert?.(product), [onInsert, product]);

  return (
    <article className="product-card">
      <div className="product-card__media">
        {imageUrl ? (
          <img src={imageUrl} alt={name} className="product-card__image" loading="lazy" />
        ) : (
          <ImageIcon className="product-card__placeholder" aria-hidden />
        )}
        {onInsert ? (
          <button
            type="button"
            className="product-card__action"
            onClick={handleInsert}
            aria-label={t(`${PRODUCTS_KEY}.insertLabel`, { name })}
          >
            <Plus aria-hidden />
          </button>
        ) : (
          productUrl && (
            <a
              href={productUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="product-card__action"
              aria-label={t(`${PRODUCTS_KEY}.viewLabel`, { name })}
            >
              <ExternalLink aria-hidden />
            </a>
          )
        )}
      </div>
      <span className="product-card__type">{productType}</span>
      <h4 className="product-card__name">{name}</h4>
      {showMatch && (
        <span
          className="product-card__share"
          title={t(`${PRODUCTS_KEY}.shareLabel`, { share })}
        >
          {t(`${PRODUCTS_KEY}.match`, { share })}
        </span>
      )}
    </article>
  );
};

interface RecommendedProductsProps {
  state: RecommendedProductsState;
  onInsertProduct?: (_product: RecommendedProduct) => void;
  onInsertAll?: () => void;
  brandName?: string;
}

const RecommendedProducts: FC<RecommendedProductsProps> = ({
  state,
  onInsertProduct,
  onInsertAll,
  brandName,
}) => {
  const { t, i18n } = useTranslation();
  const listId = useId();
  const [isOpen, setIsOpen] = useState(true);
  const toggle = useCallback(() => setIsOpen(open => !open), []);
  const { status, statusMessage, products, audience, isBestSellers, hasNoMatchedCustomers, reload } =
    state;
  const brand =
    brandName || localStorage.getItem("company_name") || t(`${PRODUCTS_KEY}.yourStore`);

  if (status === "idle") {
    return null;
  }

  const ToggleIcon = isOpen ? ChevronDown : ChevronUp;
  const isReady = status === "ready";

  const renderContent = () => {
    if (status === "loading") {
      return (
        <div className="recommended-products__state">
          <MiniSpinner />
        </div>
      );
    }
    if (!isReady) {
      const fallbackKey = status === "error" ? "loadFailed" : "empty";
      return (
        <div className="recommended-products__state">
          <p className="recommended-products__message">
            {statusMessage || t(`${PRODUCTS_KEY}.${fallbackKey}`)}
          </p>
          {status === "error" && (
            <button type="button" className="recommended-products__link" onClick={reload}>
              {t(`${PRODUCTS_KEY}.retry`)}
            </button>
          )}
        </div>
      );
    }

    return (
      <ul className="recommended-products__list">
        {products.map(product => (
          <li key={product.product_id}>
            <ProductCard
              product={product}
              onInsert={onInsertProduct}
              showMatch={!isBestSellers}
            />
          </li>
        ))}
      </ul>
    );
  };

  return (
    <section className="recommended-products">
      <div className="recommended-products__header">
        <button
          type="button"
          className="recommended-products__toggle"
          aria-expanded={isOpen}
          aria-controls={listId}
          onClick={toggle}
        >
          <ToggleIcon className="recommended-products__chevron" aria-hidden />
          <span className="recommended-products__heading">
            <span className="recommended-products__title">
              {isBestSellers ? t(`${PRODUCTS_KEY}.bestSellerTitle`, { brand }) : t(`${PRODUCTS_KEY}.title`)}
              {isBestSellers && hasNoMatchedCustomers && (
                <span className="recommended-products__note">
                  {t(`${PRODUCTS_KEY}.noPurchaseHistory`)}
                </span>
              )}
            </span>
            <span className="recommended-products__subtitle">
              {isBestSellers ? t(`${PRODUCTS_KEY}.bestSellerSubtitle`, { brand }) : t(`${PRODUCTS_KEY}.subtitle`)}
            </span>
            {isReady && audience && !isBestSellers && (
              <span className="recommended-products__caption">
                {t(`${PRODUCTS_KEY}.basedOn`, {
                  matched: formatNumber(audience.matched_customers, {}, i18n.language),
                  total: formatNumber(audience.total_contacts, {}, i18n.language),
                })}
              </span>
            )}
          </span>
        </button>
        {isOpen && isReady && onInsertAll && (
          <button type="button" className="recommended-products__link" onClick={onInsertAll}>
            {t(`${PRODUCTS_KEY}.insertAll`)}
          </button>
        )}
      </div>
      {isOpen && <div id={listId}>{renderContent()}</div>}
    </section>
  );
};

export default memo(RecommendedProducts);
